import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import {
  STATUS_LABELS,
  TIPO_LABELS,
  SOLUCOES_LABELS,
  REGIOES,
  MOTIVO_DESCARTE_LABELS,
  formatBRL,
  formatDate,
  type RadarOportunidade,
} from '@/pages/painel/lib/radarConstants';
import RadarScore from './RadarScore';
import RadarOportunidadeFormModal from './RadarOportunidadeFormModal';

interface Filtros {
  search: string;
  status: string;
  tipo: string;
  regiao: string;
  motor: string;
  solucao: string;
  faixa: string;
  valorMin: string;
  semProximaAcao: boolean;
  compra90: boolean;
}

const FILTROS_INICIAIS: Filtros = {
  search: '',
  status: 'ativas',
  tipo: '',
  regiao: '',
  motor: '',
  solucao: '',
  faixa: '',
  valorMin: '',
  semProximaAcao: false,
  compra90: false,
};

export default function RadarOportunidades() {
  const { password } = useOutletContext<{ password: string }>();
  const [oportunidades, setOportunidades] = useState<RadarOportunidade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_INICIAIS);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<RadarOportunidade | null>(null);
  const [processandoId, setProcessandoId] = useState<number | null>(null);
  const [discardTarget, setDiscardTarget] = useState<RadarOportunidade | null>(null);
  const [discardMotivo, setDiscardMotivo] = useState('outro');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const body: any = { action: 'listar', password, filtros: {} };
      const f: any = {};
      if (filtros.status) f.status = filtros.status;
      if (filtros.tipo) f.tipo = filtros.tipo;
      if (filtros.regiao) f.regiao = filtros.regiao;
      if (filtros.motor) f.motor = filtros.motor;
      if (filtros.solucao) f.solucao = filtros.solucao;
      if (filtros.faixa) f.faixa_score = filtros.faixa;
      if (filtros.valorMin) f.valor_min = Number(filtros.valorMin);
      if (filtros.semProximaAcao) f.sem_proxima_acao = true;
      if (filtros.compra90) f.compra_90dias = true;
      if (filtros.search) f.search = filtros.search;
      body.filtros = f;

      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', { body });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { oportunidades?: RadarOportunidade[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setOportunidades(parsed?.oportunidades ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar oportunidades.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, filtros]);

  const confirmarDescarte = async () => {
    if (!discardTarget) return;
    setProcessandoId(discardTarget.id);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'atualizar_status', password, oportunidade_id: discardTarget.id, status: 'descartada', motivo_descarte: discardMotivo },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setDiscardTarget(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao descartar.');
    } finally {
      setProcessandoId(null);
    }
  };

  const excluir = async (id: number) => {
    setProcessandoId(id);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'excluir', password, oportunidade_id: id },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao excluir.');
    } finally {
      setProcessandoId(null);
    }
  };

  const setF = (k: keyof Filtros, v: any) => setFiltros((prev) => ({ ...prev, [k]: v }));

  const filtrosAtivos = useMemo(
    () => Object.entries(filtros).some(([k, v]) => (k === 'status' ? v !== 'ativas' : v !== '' && v !== false)),
    [filtros],
  );

  const solucoesUsadas = useMemo(() => {
    const set = new Set<string>();
    oportunidades.forEach((o) => (o.solucoes ?? []).forEach((s) => set.add(s)));
    return Array.from(set);
  }, [oportunidades]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            <strong className="text-gray-900">{oportunidades.length}</strong> oportunidade(s)
          </span>
        </div>
        <button
          onClick={() => { setEditando(null); setModalOpen(true); }}
          className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-add-line"></i>
          Nova oportunidade
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={filtros.search}
            onChange={(e) => setF('search', e.target.value)}
            placeholder="Buscar por título, empresa, cidade, segmento..."
            className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all bg-white"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Status</label>
            <select value={filtros.status} onChange={(e) => setF('status', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
              <option value="ativas">Ativas</option>
              <option value="todas">Todas</option>
              <option value="descartada">Descartadas</option>
              <option value="convertida_crm">Convertidas p/ CRM</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Tipo</label>
            <select value={filtros.tipo} onChange={(e) => setF('tipo', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
              <option value="">Todos</option>
              {Object.entries(TIPO_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Região</label>
            <select value={filtros.regiao} onChange={(e) => setF('regiao', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
              <option value="">Todas</option>
              {REGIOES.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Solução HOP</label>
            <select value={filtros.solucao} onChange={(e) => setF('solucao', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
              <option value="">Todas</option>
              {solucoesUsadas.map((s) => (
                <option key={s} value={s}>{SOLUCOES_LABELS[s] ?? s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Motor</label>
            <select value={filtros.motor} onChange={(e) => setF('motor', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
              <option value="">Ambos</option>
              <option value="caixa">Motor Caixa</option>
              <option value="grandes_projetos">Motor Grandes Projetos</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Valor mínimo (R$)</label>
            <input
              type="number"
              value={filtros.valorMin}
              onChange={(e) => setF('valorMin', e.target.value)}
              placeholder="Ex: 500000"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Score</label>
            <select value={filtros.faixa} onChange={(e) => setF('faixa', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
              <option value="">Todos</option>
              <option value="quente">Quentes (70+)</option>
              <option value="monitorar">Monitorar (50–69)</option>
              <option value="baixa">Baixa (&lt;50)</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setF('semProximaAcao', !filtros.semProximaAcao)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border cursor-pointer whitespace-nowrap transition-colors ${
              filtros.semProximaAcao ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]/40'
            }`}
          >
            Sem próxima ação
          </button>
          <button
            onClick={() => setF('compra90', !filtros.compra90)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border cursor-pointer whitespace-nowrap transition-colors ${
              filtros.compra90 ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]/40'
            }`}
          >
            Venda em 90 dias
          </button>
          {filtrosAtivos && (
            <button onClick={() => setFiltros(FILTROS_INICIAIS)} className="text-xs font-semibold text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">
              <i className="ri-close-circle-line mr-1"></i>Limpar filtros
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && oportunidades.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : oportunidades.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-radar-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500 mb-1">Nenhuma oportunidade com estes filtros.</p>
          <p className="text-xs text-gray-400">Crie uma nova oportunidade ou ajuste os filtros.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {oportunidades.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-gray-200 p-4 hover:border-[#E8610A]/30 transition-colors">
              <div className="flex items-start gap-4">
                <RadarScore op={o} size="sm" />
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/painel/radar/oportunidades/${o.id}`}
                    className="font-semibold text-gray-900 hover:text-[#E8610A] transition-colors"
                  >
                    {o.titulo}
                  </Link>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {o.empresa && <span className="text-xs text-gray-500">{o.empresa}</span>}
                    {o.tipo && (
                      <span className="text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                        {TIPO_LABELS[o.tipo] ?? o.tipo}
                      </span>
                    )}
                    <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${
                      o.motor === 'grandes_projetos' ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'
                    }`}>
                      {o.motor === 'grandes_projetos' ? 'Motor Grandes Projetos' : 'Motor Caixa'}
                    </span>
                    <span className="text-[11px] font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                      {STATUS_LABELS[o.status] ?? o.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {[o.cidade, o.uf].filter(Boolean).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                        <i className="ri-map-pin-line"></i>
                        {[o.cidade, o.uf].filter(Boolean).join(' - ')}
                      </span>
                    )}
                    {(o.solucoes ?? []).slice(0, 4).map((s) => (
                      <span key={s} className="text-[10px] text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                        {SOLUCOES_LABELS[s] ?? s}
                      </span>
                    ))}
                    {o.proxima_acao && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <i className="ri-flag-2-line"></i>
                        {o.proxima_acao}
                        {o.proxima_acao_em && <span className="text-gray-400">· {formatDate(o.proxima_acao_em)}</span>}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-gray-900">{formatBRL(o.valor_potencial_hop ?? o.valor_obra)}</p>
                  {o.lucro_potencial != null && (
                    <p className="text-xs text-green-600">{formatBRL(o.lucro_potencial)} lucro</p>
                  )}
                  <div className="flex items-center gap-1 justify-end mt-1.5">
                    <button
                      onClick={() => { setEditando(o); setModalOpen(true); }}
                      className="p-1.5 text-gray-400 hover:text-[#E8610A] hover:bg-[#E8610A]/10 rounded-lg cursor-pointer"
                      title="Editar"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    {o.status !== 'descartada' && (
                      <button
                        onClick={() => { setDiscardTarget(o); setDiscardMotivo('outro'); }}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg cursor-pointer"
                        title="Descartar"
                      >
                        <i className="ri-archive-line text-sm"></i>
                      </button>
                    )}
                    <button
                      onClick={() => excluir(o.id)}
                      disabled={processandoId === o.id}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer disabled:opacity-50"
                      title="Excluir"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <RadarOportunidadeFormModal
          op={editando}
          password={password}
          onClose={() => { setModalOpen(false); setEditando(null); }}
          onSaved={() => { setModalOpen(false); setEditando(null); load(); }}
        />
      )}

      {discardTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDiscardTarget(null)}>
          <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm">Descartar oportunidade</h3>
              <button onClick={() => setDiscardTarget(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                <strong>{discardTarget.titulo}</strong> — informe o motivo do descarte. Isso ajuda a IA a aprender quais oportunidades não valem seu tempo.
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Motivo</label>
                <select value={discardMotivo} onChange={(e) => setDiscardMotivo(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
                  {Object.entries(MOTIVO_DESCARTE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDiscardTarget(null)} className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap">
                  Cancelar
                </button>
                <button
                  onClick={confirmarDescarte}
                  disabled={processandoId === discardTarget.id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {processandoId === discardTarget.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-archive-line"></i>}
                  Descartar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}